import { useEffect, useState, FormEvent } from 'react';
import {
  Mail,
  Plus,
  Trash2,
  Pencil,
  PlayCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from './admin/ConfirmDialog';
import { MailMonitorInbox } from './MailMonitorInbox';

type MailAccount = {
  id: number;
  label: string;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  forwardEmail: string | null;
  active: boolean;
  ownerUserId: number;
  ownerEmail: string | null;
  teamOwnerEmail: string | null;
  teamId: number | null;
  isOwner: boolean;
  canManage: boolean;
  lastTestStatus: 'ok' | 'failed' | null;
  lastTestError: string | null;
  lastTestedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

type FormState = {
  label: string;
  host: string;
  port: string;
  secure: boolean;
  username: string;
  forwardEmail: string;
  password: string;
};

const DEFAULT_FORM: FormState = {
  label: '',
  host: '',
  port: '993',
  secure: true,
  username: '',
  forwardEmail: '',
  password: '',
};

interface Props {
  darkMode: boolean;
  cardClasses: string;
  inputClasses: string;
}

export function MailMonitorSettings({ darkMode, cardClasses, inputClasses }: Props) {
  const { user } = useAuth();
  const canManage = user?.isAdmin === true || user?.teamRole === 'owner' || user?.teamRole === 'admin';
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<MailAccount | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<MailAccount | null>(null);

  const setBusy = (id: number, on: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await api.get<MailAccount[]>('/api/mail-monitor');
      setAccounts(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const openCreate = () => {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
  };

  const openEdit = (acc: MailAccount) => {
    setEditing(acc);
    setForm({
      label: acc.label,
      host: acc.host,
      port: String(acc.port),
      secure: acc.secure,
      username: acc.username,
      forwardEmail: acc.forwardEmail ?? '',
      password: '', // leer = unverändert
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const port = parseInt(form.port, 10);
      if (!Number.isFinite(port) || port < 1) {
        throw new Error('Ungültiger Port');
      }
      if (editing) {
        const payload: Record<string, unknown> = {
          label: form.label,
          host: form.host,
          port,
          secure: form.secure,
          username: form.username,
        };
        // Leer lassen = nicht ändern (wie Passwort) — @IsEmail() im Backend würde einen
        // leeren String sonst als ungültig ablehnen.
        if (form.forwardEmail.trim().length > 0) payload.forwardEmail = form.forwardEmail.trim();
        if (form.password.length > 0) payload.password = form.password;
        await api.patch(`/api/mail-monitor/${editing.id}`, payload);
        setSuccess('Account aktualisiert');
      } else {
        if (form.password.length === 0) {
          throw new Error('Passwort ist erforderlich');
        }
        const payload: Record<string, unknown> = {
          label: form.label,
          host: form.host,
          port,
          secure: form.secure,
          username: form.username,
          password: form.password,
        };
        if (form.forwardEmail.trim().length > 0) payload.forwardEmail = form.forwardEmail.trim();
        await api.post('/api/mail-monitor', payload);
        setSuccess('Account angelegt');
      }
      setShowForm(false);
      setEditing(null);
      setForm(DEFAULT_FORM);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (acc: MailAccount) => {
    setBusy(acc.id, true);
    setError('');
    try {
      await api.patch(`/api/mail-monitor/${acc.id}/active`, { active: !acc.active });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Umschalten');
    } finally {
      setBusy(acc.id, false);
    }
  };

  const handleTest = async (acc: MailAccount) => {
    setBusy(acc.id, true);
    setError('');
    try {
      const updated = await api.post<MailAccount>(`/api/mail-monitor/${acc.id}/test`);
      setAccounts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      if (updated.lastTestStatus === 'ok') {
        setSuccess('Verbindung erfolgreich');
      } else {
        setError(`Verbindung fehlgeschlagen: ${updated.lastTestError ?? 'Unbekannter Fehler'}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Verbindungstest');
    } finally {
      setBusy(acc.id, false);
    }
  };

  const handleDelete = (acc: MailAccount) => {
    setDeleteConfirm(acc);
  };

  const performDelete = async (acc: MailAccount) => {
    setDeleteConfirm(null);
    setBusy(acc.id, true);
    setError('');
    try {
      await api.delete(`/api/mail-monitor/${acc.id}`);
      setSuccess('Account gelöscht');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen');
    } finally {
      setBusy(acc.id, false);
    }
  };

  const statusBadge = (acc: MailAccount) => {
    if (acc.lastTestStatus === 'ok') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Verbunden
        </span>
      );
    }
    if (acc.lastTestStatus === 'failed') {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs font-medium text-red-700 dark:text-red-400"
          title={acc.lastTestError ?? undefined}
        >
          <XCircle className="w-3.5 h-3.5" />
          Fehler
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
        <Clock className="w-3.5 h-3.5" />
        Ungetestet
      </span>
    );
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4" translate="no">
      <div className={cardClasses}>
        <div className="flex items-center justify-between mb-5 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                darkMode ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}
            >
              <Mail className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div className="min-w-0">
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Mail / Monitor
              </h2>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                IMAP-Postfächer für die Überwachung von BAFA-Entscheidungen und Nachrichten
              </p>
            </div>
          </div>
          {accounts.length === 0 && canManage && (
            <button
              type="button"
              onClick={openCreate}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex-shrink-0"
            >
              <span className="inline-flex">
                <Plus className="w-4 h-4" />
              </span>
              <span>Postfach einrichten</span>
            </button>
          )}
        </div>

        {accounts.length === 0 && !canManage && (
          <p className={`mb-4 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'} italic`}>
            Noch kein Postfach für dieses Team eingerichtet. Bitten Sie Ihren Team-Owner oder -Admin, eines anzulegen.
          </p>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 text-sm">
            {success}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Wird geladen…</span>
          </div>
        ) : accounts.length === 0 ? (
          <div
            className={`text-sm rounded-lg border border-dashed p-6 text-center ${
              darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'
            }`}
          >
            Noch keine Mail-Accounts eingerichtet.
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map((acc) => {
              const busy = busyIds.has(acc.id);
              return (
                <div
                  key={acc.id}
                  className={`rounded-lg border p-4 ${
                    darkMode ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50/60'
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                          {acc.label}
                        </span>
                        {statusBadge(acc)}
                        {!acc.active && (
                          <span
                            className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${
                              darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'
                            }`}
                          >
                            Inaktiv
                          </span>
                        )}
                      </div>
                      <div
                        className={`text-xs mt-1 break-all ${
                          darkMode ? 'text-slate-400' : 'text-slate-500'
                        }`}
                      >
                        {acc.username} · {acc.host}:{acc.port} {acc.secure ? '(SSL)' : '(STARTTLS/Plain)'}
                        {acc.forwardEmail && <> · Weiterleitungsadresse: {acc.forwardEmail}</>}
                      </div>
                      <div className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Team-Inhaber: {acc.teamOwnerEmail ?? '—'}{acc.ownerEmail && acc.ownerEmail !== acc.teamOwnerEmail ? ` (eingerichtet von ${acc.ownerEmail})` : ''}
                        {acc.lastTestedAt && (
                          <span> · Zuletzt getestet: {formatDate(acc.lastTestedAt)}</span>
                        )}
                      </div>
                      {acc.lastTestStatus === 'failed' && acc.lastTestError && (
                        <div className="text-xs mt-1 text-red-600 dark:text-red-400 break-all">
                          {acc.lastTestError}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 md:flex-shrink-0">
                      {/* Active-Toggle: jeder Nutzer */}
                      <label
                        className={`inline-flex items-center gap-2 text-xs cursor-pointer select-none ${
                          darkMode ? 'text-slate-300' : 'text-slate-600'
                        }`}
                        title="Aktiv / Inaktiv umschalten"
                      >
                        <input
                          type="checkbox"
                          checked={acc.active}
                          disabled={busy || !acc.canManage}
                          onChange={() => handleToggle(acc)}
                          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                        <span>Aktiv</span>
                      </label>

                      <button
                        type="button"
                        onClick={() => handleTest(acc)}
                        disabled={busy || !acc.canManage}
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors disabled:opacity-50 ${
                          darkMode
                            ? 'border-slate-600 text-slate-200 hover:bg-slate-700'
                            : 'border-slate-300 text-slate-700 hover:bg-white'
                        }`}
                        title="Verbindung testen"
                      >
                        <span className="inline-flex">
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                        </span>
                        <span>Test</span>
                      </button>

                      {acc.canManage && (
                        <>
                          <button
                            type="button"
                            onClick={() => openEdit(acc)}
                            disabled={busy}
                            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors disabled:opacity-50 ${
                              darkMode
                                ? 'border-slate-600 text-slate-200 hover:bg-slate-700'
                                : 'border-slate-300 text-slate-700 hover:bg-white'
                            }`}
                            title="Bearbeiten"
                          >
                            <span className="inline-flex">
                              <Pencil className="w-3.5 h-3.5" />
                            </span>
                            <span>Bearbeiten</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(acc)}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                            title="Löschen"
                          >
                            <span className="inline-flex">
                              <Trash2 className="w-3.5 h-3.5" />
                            </span>
                            <span>Löschen</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Account löschen"
        message={deleteConfirm ? `Account "${deleteConfirm.label}" wirklich löschen?` : ''}
        confirmLabel="Löschen"
        onConfirm={() => deleteConfirm && performDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />

      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !submitting && setShowForm(false)}
        >
          <div
            className={`w-full max-w-lg rounded-xl shadow-lg ${
              darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {editing ? 'Account bearbeiten' : 'Neuer IMAP-Account'}
              </h3>
              <button
                type="button"
                onClick={() => !submitting && setShowForm(false)}
                className={`p-1 rounded-md ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
              >
                <X className={`w-5 h-5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Bezeichnung
                </label>
                <input
                  type="text"
                  required
                  placeholder="z.B. BAFA-Entscheidungen Postfach"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  className={inputClasses}
                  translate="no"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    IMAP-Server (Host)
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="imap.example.com"
                    value={form.host}
                    onChange={(e) => setForm({ ...form, host: e.target.value })}
                    className={inputClasses}
                    translate="no"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Port
                  </label>
                  <input
                    type="number"
                    required
                    min={1}
                    value={form.port}
                    onChange={(e) => setForm({ ...form, port: e.target.value })}
                    className={inputClasses}
                    translate="no"
                  />
                </div>
              </div>
              <div>
                <label className={`inline-flex items-center gap-2 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  <input
                    type="checkbox"
                    checked={form.secure}
                    onChange={(e) => setForm({ ...form, secure: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  SSL/TLS verwenden (empfohlen, Standard-Port 993)
                </label>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Benutzername
                </label>
                <input
                  type="text"
                  required
                  autoComplete="off"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className={inputClasses}
                  translate="no"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  E-Mail-Adresse des Postfachs
                  <span className="text-xs text-slate-400 font-normal"> (nur nötig, falls der Benutzername oben keine echte Mailadresse ist)</span>
                </label>
                <input
                  type="email"
                  placeholder="team@firma.de"
                  autoComplete="off"
                  value={form.forwardEmail}
                  onChange={(e) => setForm({ ...form, forwardEmail: e.target.value })}
                  className={inputClasses}
                  translate="no"
                />
                <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Wird Kunden als Weiterleitungsadresse für BAFA-Einmal-Links gezeigt. Manche Hoster vergeben
                  Logins wie „m0703fa4", die keine Mailadresse sind — in dem Fall hier die echte Adresse eintragen.
                </p>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Passwort {editing && <span className="text-xs text-slate-400">(leer = unverändert)</span>}
                </label>
                <input
                  type="password"
                  required={!editing}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={inputClasses}
                  translate="no"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => !submitting && setShowForm(false)}
                  disabled={submitting}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    darkMode
                      ? 'border-slate-600 text-slate-200 hover:bg-slate-700'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  } disabled:opacity-50`}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <span className="inline-flex">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  </span>
                  <span>{editing ? 'Speichern' : 'Anlegen'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <MailMonitorInbox darkMode={darkMode} cardClasses={cardClasses} />
    </div>
  );
}
